import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { InspectionTypes } from '../models/Inspection';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

export class InspectionDraftModel {
  id: number;
  draftNumber: string;
  inspectionTypeID: number;
  inspectionTypeCode?: string;
  inspectionTypeName?: string;
  inspectionType?: {
    id: number;
    name: string;
    inspectionTypeCode: string;
  };
  inspectionJsonData: string;
  draftData?: any;
  facilityName?: string;
  manufacturerName?: string;
  savedBy: number;
  createdBy: number;
  createdDate: Date | string;
  modifiedDate: Date | string;
  isActive: boolean;
  rowGuid?: string;

  constructor(jsonData?: string, userId?: number, inspectionTypeId?: number) {
    this.inspectionJsonData = jsonData || '{}';
    this.createdBy = userId;
    this.savedBy = userId;
    this.inspectionTypeID = inspectionTypeId || 0;
    this.draftNumber = '/Draft/' + new Date().getFullYear();
    this.isActive = true;
    this.createdDate = new Date();
    this.modifiedDate = new Date();
  }
}

@Injectable({
  providedIn: 'root'
})
export class InspectionDraftService {
  private draftUrl = environment.baseUrl + '/InspectionDraft';
  private localPrefix = 'eris_inspection_drafts_';

  constructor(private http: HttpClient) {}

  private getLocalDrafts(userId: number): InspectionDraftModel[] {
    try {
      const raw = localStorage.getItem(`${this.localPrefix}${userId}`);
      if (!raw) return [];
      const drafts: InspectionDraftModel[] = JSON.parse(raw);
      return drafts;
    } catch {
      return [];
    }
  }

  private saveLocalDrafts(userId: number, drafts: InspectionDraftModel[]) {
    try {
      localStorage.setItem(`${this.localPrefix}${userId}`, JSON.stringify(drafts));
    } catch (e) {
      console.warn('LocalStorage save failed', e);
    }
  }

  private purgeExpiredDrafts(drafts: InspectionDraftModel[], userId: number): InspectionDraftModel[] {
    const cutoff = Date.now() - 45 * 24 * 60 * 60 * 1000;
    const active = drafts.filter(d => {
      if (d.id && d.id > 0) {
        return true;
      }
      const ts = new Date(d.modifiedDate || d.createdDate).getTime();
      return !(ts < cutoff);
    });

    if (active.length !== drafts.length) {
      this.saveLocalDrafts(userId, active);
    }

    return active;
  }

  public parseDraftData(draft: InspectionDraftModel): InspectionDraftModel {
    if (!draft) return draft;
    if (!draft.id && (draft as any)?.ID) {
      draft.id = (draft as any).ID;
    }
    try {
      if (draft.inspectionJsonData) {
        draft.draftData = JSON.parse(draft.inspectionJsonData);
        if (draft.draftData) {
          // Facility name for Local GMP
          if (draft.draftData.facilities?.facility?.name) {
            draft.facilityName = draft.draftData.facilities.facility.name;
          } else if (draft.draftData.facilityInfo?.name) {
            draft.facilityName = draft.draftData.facilityInfo.name;
          }

          // Manufacturer / Local Agent for Abroad CGMP
          if (draft.draftData.facilityInformation?.manufacturer?.name) {
            draft.manufacturerName = draft.draftData.facilityInformation.manufacturer.name;
          } else if (draft.draftData.localAgentInformation?.localAgentName) {
            draft.manufacturerName = draft.draftData.localAgentInformation.localAgentName;
          }
        }
      }
    } catch (e) {
      console.warn('Error parsing inspection draft JSON', e);
    }

    if (!draft.inspectionTypeCode && draft.inspectionType?.inspectionTypeCode) {
      draft.inspectionTypeCode = draft.inspectionType.inspectionTypeCode;
    }
    if (draft.inspectionTypeCode && !draft.inspectionTypeName) {
      draft.inspectionTypeName = InspectionTypes.codeToName(draft.inspectionTypeCode) || draft.inspectionType?.name;
    }

    return draft;
  }

  getDraftsByUser(userId: number): Observable<InspectionDraftModel[]> {
    return this.http.get<InspectionDraftModel[]>(`${this.draftUrl}/GetByUser/${userId}`, httpOptions).pipe(
      map(res => {
        const parsed = (res || []).map(d => this.parseDraftData(d));
        const active = this.purgeExpiredDrafts(parsed, userId);
        this.saveLocalDrafts(userId, active);
        return active;
      }),
      catchError(() => {
        // Fallback to local storage
        const local = this.getLocalDrafts(userId).map(d => this.parseDraftData(d));
        const active = this.purgeExpiredDrafts(local, userId);
        return of(active);
      })
    );
  }

  getDraftsByUserAndType(userId: number, typeCode: string): Observable<InspectionDraftModel[]> {
    const url = `${this.draftUrl}/GetByUserAndType/${userId}/${typeCode}`;
    return this.http.get<InspectionDraftModel[]>(url, httpOptions).pipe(
      map(res => {
        const parsed = (res || []).map(d => this.parseDraftData(d));
        const active = this.purgeExpiredDrafts(parsed, userId);
        return active;
      }),
      catchError(() => {
        // Fallback to local storage filter
        return this.getDraftsByUser(userId).pipe(
          map(drafts => {
            if (!typeCode || typeCode.toUpperCase() === 'ALL') return drafts;
            return drafts.filter(d => {
              if (d.inspectionTypeCode) {
                return d.inspectionTypeCode.toUpperCase() === typeCode.toUpperCase();
              }
              return true;
            });
          })
        );
      })
    );
  }

  getDraft(id: number): Observable<InspectionDraftModel> {
    return this.http.get<InspectionDraftModel>(`${this.draftUrl}/${id}`, httpOptions).pipe(
      map(res => this.parseDraftData(res)),
      catchError(() => {
        // Find in local storage
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(this.localPrefix)) {
            try {
              const list: InspectionDraftModel[] = JSON.parse(localStorage.getItem(key) || '[]');
              const match = list.find(d => d.id === id);
              if (match) return of(this.parseDraftData(match));
            } catch {}
          }
        }
        return of(null as any);
      })
    );
  }

  saveDraft(draft: InspectionDraftModel): Observable<InspectionDraftModel> {
    draft.modifiedDate = new Date();
    return this.http.post<InspectionDraftModel>(`${this.draftUrl}/SaveAsDraft`, draft, httpOptions).pipe(
      map(res => {
        if (res) {
          if ((res as any).ID && !res.id) {
            res.id = (res as any).ID;
          }
          const parsed = this.parseDraftData(res);
          if (!parsed.id && draft.id) {
            parsed.id = draft.id;
          }
          if (!parsed.inspectionTypeCode && draft.inspectionTypeCode) {
            parsed.inspectionTypeCode = draft.inspectionTypeCode;
          }
          if (!parsed.inspectionTypeName && draft.inspectionTypeName) {
            parsed.inspectionTypeName = draft.inspectionTypeName;
          }
          const userId = parsed.createdBy || parsed.savedBy || draft.createdBy;
          const list = this.getLocalDrafts(userId).filter(d => d.id !== parsed.id && (!draft.id || d.id !== draft.id));
          list.unshift(parsed);
          this.saveLocalDrafts(userId, list);
          return parsed;
        }
        return draft;
      }),
      catchError(err => {
        console.warn('API SaveAsDraft failed, persisting draft locally:', err);
        const userId = draft.createdBy || draft.savedBy || 0;
        if (!draft.id || draft.id === 0) {
          draft.id = -Math.floor(Date.now() % 10000000);
        }
        const parsed = this.parseDraftData(draft);
        const list = this.getLocalDrafts(userId).filter(d => d.id !== parsed.id);
        list.unshift(parsed);
        this.saveLocalDrafts(userId, list);
        return of(parsed);
      })
    );
  }

  deleteDraft(id: number, userId?: number): Observable<boolean> {
    const removeLocal = () => {
      if (userId) {
        const list = this.getLocalDrafts(userId).filter(d => d.id !== id);
        this.saveLocalDrafts(userId, list);
        return;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.localPrefix)) {
          try {
            const list: InspectionDraftModel[] = JSON.parse(localStorage.getItem(key) || '[]');
            const updated = list.filter(d => d.id !== id);
            localStorage.setItem(key, JSON.stringify(updated));
          } catch {}
        }
      }
    };

    if (id < 0) {
      removeLocal();
      return of(true);
    }

    return this.http.delete<boolean>(`${this.draftUrl}/${id}`, httpOptions).pipe(
      map(res => {
        const deleted = !!res;
        if (deleted) {
          removeLocal();
        }
        return deleted;
      }),
      catchError(() => of(false))
    );
  }
}
